/**
 * A menu in a further language (S36, docs/15-sprachen.md): one set of translations for print, PDF,
 * Word and the net. A translation remembers a fingerprint of the German text it was made from; when
 * that text changes it is out of date, and only that dish needs translating again. Translated by
 * hand or by the model (`by`); a hand-made one is never overwritten without asking.
 *
 * Fixed texts need no model: the names of the 14 allergens are official in every EU language (annex
 * II LMIV), the additives and the default footer come from the tables below, as long as the business
 * kept the German default. English first; the other languages follow (S36).
 */
import type { Allergen } from "./allergens.ts";
import { DEFAULT_ALLERGENS } from "./allergens.ts";
import type { Phrases } from "./phrases.ts";
import { phraseOf } from "./phrase-base.ts";
import { LANGS, LANG_LABELS, dietTagIn, isLang } from "@household-kit/lang";
import type { Lang } from "@household-kit/lang";
import { allergenName as officialName, byCode } from "@household-kit/food";

// The languages, their names and short forms, from household-kit (lang, S36; geprüfte Kopie in packages/shared).
export { LANGS, LANG_LABELS, LANG_NATIVE, LANG_SHORT, isLang } from "@household-kit/lang";
export type { Lang } from "@household-kit/lang";
/** The languages a business can switch on: all six since 01.11.2026 (Martin; S36: English first). */
export const OFFERED_LANGS: readonly Lang[] = LANGS;

export interface Translation {
  /** Empty: the name as it is (S36 § 3, „Name nicht übersetzen“, or not translated yet). */
  name: string;
  description: string;
  /** Fingerprint of the German text it was made from (`sourceOf`). */
  source: string;
  /** By hand, by the model, or by the model and looked through („Passt“, S61): only „ai“ is still to be checked. */
  by: "hand" | "ai" | "checked";
}
export type Translations = Partial<Record<Lang, Translation>>;
/** What a form or a request gives: the source and who made it may be missing. */
export type TranslationsInput = Partial<Record<Lang, Partial<Translation>>>;

export const TRANSLATION_LIMITS = { name: 120, description: 600 } as const;

const oneLine = (s: unknown) => (typeof s === "string" ? s.normalize("NFC").replace(/\s+/g, " ").trim() : "");

/** A short fingerprint of a German text (FNV-1a, 32 bits): enough to see that it changed. */
export function sourceOf(...parts: readonly string[]): string {
  let h = 0x811c9dc5;
  for (const ch of parts.map(oneLine).join("\u0000")) {
    h ^= ch.codePointAt(0)!;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/**
 * The translations as they may be stored, or what is wrong with them. A translation without a name
 * and a description is dropped. Without its own `source` it counts as made from `source`, the German
 * text saved with it (the app sends the source it translated from, so an unchanged translation of a
 * changed text stays out of date).
 */
export function cleanTranslations(raw: unknown, source: string): { value: Translations } | { error: string } {
  if (raw === undefined || raw === null) return { value: {} };
  if (typeof raw !== "object" || Array.isArray(raw)) return { error: "Die Übersetzungen passen nicht." };
  const out: Translations = {};
  for (const [lang, t] of Object.entries(raw as Record<string, unknown>)) {
    if (!isLang(lang)) return { error: `Unbekannte Sprache: ${lang}` };
    if (typeof t !== "object" || t === null) return { error: "Die Übersetzungen passen nicht." };
    const x = t as Record<string, unknown>;
    const name = oneLine(x.name);
    const description = oneLine(x.description);
    if (name.length > TRANSLATION_LIMITS.name) return { error: `Der Name auf ${LANG_LABELS[lang]} ist zu lang (höchstens ${TRANSLATION_LIMITS.name} Zeichen).` };
    if (description.length > TRANSLATION_LIMITS.description) return { error: `Die Beschreibung auf ${LANG_LABELS[lang]} ist zu lang (höchstens ${TRANSLATION_LIMITS.description} Zeichen).` };
    if (name === "" && description === "") continue;
    const own = typeof x.source === "string" && /^[0-9a-f]{8}$/.test(x.source) ? x.source : source;
    out[lang] = { name, description, source: own, by: x.by === "ai" || x.by === "checked" ? x.by : "hand" };
  }
  return { value: out };
}

/** What a dish needs for its translation: its German texts and whether its name stays as it is. */
export type Translatable = { name: string; description: string; kind?: "drink"; keepName?: boolean; translations?: Translations };

/** Whether the name stays untranslated (S36 § 3): as the dish says, else for drinks (Rioja, Estrella Galicia). */
export const keepsName = (d: Pick<Translatable, "kind" | "keepName">) => d.keepName ?? d.kind === "drink";

/** The German text a dish's translation is made from. */
export const dishSource = (d: Pick<Translatable, "name" | "description">) => sourceOf(d.name, d.description);

export type TranslationState = "none" | "missing" | "stale" | "done";

/** How far a dish is translated: not at all, partly, made from an older German text, or done. */
export function translationState(d: Translatable, lang: Lang): TranslationState {
  // A name that stays and no description (a beer, a water): nothing to translate.
  if (keepsName(d) && d.description === "") return "done";
  const t = d.translations?.[lang];
  if (!t) return "none";
  if ((!keepsName(d) && t.name === "") || (d.description !== "" && t.description === "")) return "missing";
  return t.source === dishSource(d) ? "done" : "stale";
}

/** A dish's texts in a language, the German ones where a translation is missing. */
export function dishIn(d: Translatable, lang: Lang): { name: string; description: string; missing: boolean } {
  const t = d.translations?.[lang];
  const name = keepsName(d) ? d.name : t?.name || d.name;
  const description = d.description === "" ? "" : t?.description || d.description;
  return { name, description, missing: translationState(d, lang) !== "done" };
}

// ---------------------------------------------------------------------------
// Fixed texts
// ---------------------------------------------------------------------------

/*
 * The 14 allergens of the LMIV (annex II) by language come from household-kit (food): official, word
 * for word from Regulation (EU) 1169/2011 (EUR-Lex), fixed; they hold while the business keeps the
 * German default. The additives are not set by the EU (Martin, 01.11.2026): their translations are a
 * default in the base vocabulary (household-kit lang) a business may change.
 */

/**
 * An allergen's name in a language: one of the 14 from the official table while it is the default's;
 * else – an additive, an own one – from the business's dictionary (`phrases.ts`) or the base
 * vocabulary, else its own (German).
 */
export function allergenName(a: Pick<Allergen, "code" | "name"> & { translations?: Partial<Record<Lang, string>> }, lang: Lang, phrases: Phrases = {}): string {
  const own = a.translations?.[lang];
  if (own) return own;
  const isDefault = DEFAULT_ALLERGENS.some((d) => d.kind === "allergen" && d.code === a.code && d.name === a.name);
  const official = isDefault ? byCode(a.code) : undefined;
  return (official && "names" in official ? officialName(official.id, lang) : undefined) || phraseOf(a.name, lang, phrases)?.text || a.name;
}

/** The legend in a language. */
export const legendIn = <A extends Allergen>(legend: readonly A[], lang: Lang, phrases: Phrases = {}): A[] => legend.map((a) => ({ ...a, name: allergenName(a, lang, phrases) }));

/** The tags that tell guests about the diet, by language, from household-kit (lang). */
export { DIET_TAG_WORDS } from "@household-kit/lang";
/** A tag in a language: the diet's from the table, others from the business's dictionary or the base vocabulary, else as it is. */
export const tagIn = (tag: string, lang: Lang, phrases: Phrases = {}) => dietTagIn(tag, lang) ?? (phraseOf(tag, lang, phrases)?.text || tag);

/** The words a card prints besides the business's own, by language, from household-kit (lang). */
export { CARD_WORDS } from "@household-kit/lang";
export type { CardWords } from "@household-kit/lang";
